import React, { useState, useEffect } from "react";
import { FaStar, FaTrash, FaEdit } from "react-icons/fa";
import useAuth from "../hooks/useAuth";
import { canDo } from "../utils/canDo";
import api from "./api";
import "./ChecklistManager.css";


const ChecklistManager = ({ userFilter }) => {
  const [checklists, setChecklists] = useState([]);
  const [users, setUsers] = useState([]);
  const [editing, setEditing] = useState(null);
  const { auth } = useAuth();
  const [searchQuery, setSearchQuery] = useState("");
  const [modalOpen, setModalOpen] = useState(false);

  const todayStr = new Date().toISOString().split("T")[0];

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    assignee: "",
    frequency: "Daily",
    frequencyDays: [],
    totalWorth: 0,
    startDate: '',//todayStr,
  });

  const isSuperadmin = canDo(auth, 'checklist.edit');

    // Track which month to display for each checklist
  const [monthOffsets, setMonthOffsets] = React.useState({});

  // --- Fetch Data ---
  useEffect(() => {
    fetchChecklists();
    fetchUsers();
  }, [userFilter]); // refetch whenever the parent filter changes

  // --- Remove FE auto-generation of recurrences ---
  // useEffect(() => {
  //   const today = new Date();
  //   if (today.getDate() === 1) {
  //     checklists.forEach((item) => {
  //       if (["Daily", "Weekly"].includes(item.frequency)) {
  //         const recurrences = generateRecurrences(
  //           new Date(today.getFullYear(), today.getMonth(), 1),
  //           item.frequency,
  //           item.frequencyDays
  //         );
  //         console.log("🔁 Auto-regenerated recurrences for", item.title, recurrences);
  //         // optionally, send to backend if needed
  //       }
  //     });
  //   }
  // }, [checklists]);

  const fetchChecklists = async () => {
    try {
      // userFilter = 'all' → fetch all; specific user → by assignee; none → default to current user
      const effectiveUser = userFilter || auth?.username;
      const isAll = effectiveUser === 'all';
      const path = isAll
        ? `/checklists?_=${Date.now()}`
        : `/checklists/${effectiveUser}?_=${Date.now()}`;
      const res = await api.get(`${process.env.REACT_APP_URL}${path}`);
      setChecklists(res.data || []);
    } catch (err) {
      console.error("❌ Failed to fetch checklists", err);
      setChecklists([]);
    }
  };

    // 🧭 Utility functions
const changeMonth = (id, delta) => {
  setMonthOffsets((prev) => ({
    ...prev,
    [id]: (prev[id] ?? 0) + delta,
  }));
};

  const fetchUsers = async () => {
    try {
      const res = await api.get(`${process.env.REACT_APP_URL}/users/therapist?_=${new Date().getTime()}`);
      setUsers(res.data || []);
    } catch (err) {
      console.error("❌ Failed to fetch users", err);
      setUsers([]);
    }
  };

  // --- Toggle Weekly Days ---
  const toggleDay = (day) => {
    setFormData((prev) => {
      const days = new Set(prev.frequencyDays);
      days.has(day) ? days.delete(day) : days.add(day);
      return { ...prev, frequencyDays: Array.from(days) };
    });
  };

  const openModalForAdd = () => {
    setEditing(null); // null = adding new
    setModalOpen(true);
  };

  const openModalForEdit = (checklist) => {
    setEditing(checklist);
    setFormData({
      title: checklist.title || "",
      description: checklist.description || "",
      assignee: checklist.assignee || "",
      frequency: checklist.frequency || "Daily",
      frequencyDays: checklist.frequencyDays || [],
      totalWorth: checklist.totalWorth ?? 1,
      mainStartDate: checklist.startDate
        ? new Date(checklist.startDate).toISOString().split("T")[0]
        : "", //new Date().toISOString().split("T")[0],
      mainEndDate: checklist.endDate
        ? new Date(checklist.endDate).toISOString().split("T")[0]
        : "",      
      secondaryAssignee: checklist.secondaryAssignee || "",
      secondaryStartDate: checklist.secondaryStartDate
        ? new Date(checklist.secondaryStartDate).toISOString().split("T")[0]
        : "",
      secondaryEndDate: checklist.secondaryEndDate
        ? new Date(checklist.secondaryEndDate).toISOString().split("T")[0]
        : "",
    });
    setModalOpen(true);
  };

  // --- Mark Recurrence Done ---
  const handleRecurrenceTick = async (item, date, checked) => {
    try {
      if (!checked) return; // only handle marking done

      const now = new Date();
      const isLate = date < new Date(now.setHours(0, 0, 0, 0));

      const res = await api.patch(
        `${process.env.REACT_APP_URL}/checklists/${item._id}/complete`,
        {
          markedBy: auth.username,
          date,
          isLate,
        }
      );

      setChecklists((prev) =>
        prev.map((c) => (c._id === item._id ? res.data : c))
      );
    } catch (err) {
      console.error("❌ Failed to mark recurrence done", err);
    }
  };

  // --- Mark Recurrence as Not Applicable ---
  const handleNotApplicable = async (item, date) => {
    try {
      const res = await api.patch(
        `${process.env.REACT_APP_URL}/checklists/${item._id}/not-applicable`,
        { markedBy: auth.username, date }
      );
      setChecklists((prev) =>
        prev.map((c) => (c._id === item._id ? res.data : c))
      );
    } catch (err) {
      console.error("❌ Failed to mark as N/A", err);
    }
  };

  // --- Compute missing tasks summary ---
  const computeMissingSummary = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    let totalMissing = 0;
    const missingByTask = [];

    filteredChecklists.forEach((item) => {
      const recurrences = item.recurrences || [];
      let missed = 0;
      recurrences.forEach(({ date: dateStr }) => {
        const d = new Date(dateStr);
        d.setHours(0, 0, 0, 0);
        if (d >= today) return; // future, skip
        const completion = (item.completions || []).find(
          (c) => new Date(c.date).toDateString() === d.toDateString()
        );
        if (!completion) missed++; // N/A counts as completion (has a record), so only truly empty = missed
      });
      if (missed > 0) {
        totalMissing += missed;
        missingByTask.push({ title: item.title, assignee: item.assignee, missed });
      }
    });

    return { totalMissing, missingByTask };
  };

  // --- Delete Checklist ---
  const handleDelete = async (id) => {
    if (!window.confirm("Delete this checklist?")) return;
    try {
      await api.delete(`${process.env.REACT_APP_URL}/checklists/${id}`);
      await fetchChecklists();
    } catch (err) {
      console.error("❌ Delete failed", err);
      alert("Failed to delete checklist.");
    }
  };

  // --- Mark Entire Checklist Done ---
  const handleMarkDone = async (item) => {
    try {
      const today = new Date();
      const res = await api.patch(
        `${process.env.REACT_APP_URL}/checklists/${item._id}/complete`,
        { markedBy: auth.username, date: today }
      );
      setChecklists((prev) =>
        prev.map((c) => (c._id === item._id ? res.data : c))
      );
    } catch (err) {
      console.error("❌ Mark done failed", err);
    }
  };

  const resetForm = () =>
    setFormData({
      title: "",
      description: "",
      assignee: "",
      frequency: "Daily",
      frequencyDays: [],
      totalWorth: 0,
      startDate: '',//todayStr,
    });

  // --- Utility to generate unique ID for copied checklist ---
  const generateId = () => '_' + Math.random().toString(36).substr(2, 9);

  const modalStyles = {
    overlay: {
      position: "fixed",
      top: 0,
      left: 0,
      width: "100vw",
      height: "100vh",
      background: "rgba(0,0,0,0.5)",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      zIndex: 1000,
    },
    modal: {
      background: "#fff",
      padding: 20,
      borderRadius: 8,
      minWidth: 300,
    },
  };

  const filteredChecklists = checklists.filter((item) => {
    const q = searchQuery.toLowerCase();
    const titleMatch = item.title?.toLowerCase().includes(q);
    const descMatch = item.description?.toLowerCase().includes(q);
    const assigneeMatch = item.assignee?.toLowerCase().includes(q);

    return titleMatch || descMatch || assigneeMatch ;
  });

  // --- Save checklist including secondary assignee handling ---
  const handleSave = async (e) => {
    e.preventDefault(); // important when using form onSubmit

    try {
      const {
        title,
        assignee,
        secondaryAssignee,
        mainStartDate,
        mainEndDate,
        secondaryStartDate,
        secondaryEndDate,
        frequency,
        frequencyDays,
        description,
      } = formData;

      if (!title.trim()) {
        alert("Please enter a checklist title.");
        return;
      }

      if (secondaryAssignee && (!secondaryStartDate || !secondaryEndDate)) {
        alert("Start and End dates are required for secondary assignee.");
        return;
      }

      const safeStartDate = mainStartDate
        ? new Date(mainStartDate + "T00:00:00.000Z").toISOString()
        : "";//new Date().toISOString();

      const safeEndDate = mainEndDate
        ? new Date(mainEndDate + "T00:00:00.000Z").toISOString()
        : "";

      const secStartDate = secondaryStartDate
        ? new Date(secondaryStartDate + "T00:00:00.000Z").toISOString()
        : new Date().toISOString();

      const secEndDate = secondaryEndDate
        ? new Date(secondaryEndDate + "T00:00:00.000Z").toISOString()
        : "";

      const payload = {
        title,
        assignee,
        startDate: safeStartDate,
        endDate: safeEndDate,
        frequency,
        frequencyDays: ["Weekly", "Bi-Weekly"].includes(frequency) && (!frequencyDays || frequencyDays.length === 0)
          ? ["Mon"]
          : frequencyDays,
        description,
        user: { username: auth.username, roles: auth.roles },
        secondaryAssigneeName: secondaryAssignee,
        secondaryStartDate: secStartDate,
        secondaryEndDate: secEndDate
      };

      if (editing) {
        await api.put(`${process.env.REACT_APP_URL}/checklists/${editing._id}`, payload);
      } else {
        const res = await api.post(`${process.env.REACT_APP_URL}/checklists`, {
          ...payload,
          totalWorth: 1,
          createdBy: auth.username,
        });
      }

      // --- Secondary Assignee ---
      if (isSuperadmin && secondaryAssignee) {
        generateSecondaryRecurrences({
          ...payload,
          secondaryAssignee,
          secondaryStartDate,
          secondaryEndDate,
          _id: editing?._id,
        });
      }

      await fetchChecklists();
      resetForm();
      setEditing(null);
      setModalOpen(false);
    } catch (err) {
      console.error("❌ Save failed", err);
      alert("Failed to save checklist.");
    }
  };

  async function generateSecondaryRecurrences(originalChecklist) {
    try {
      const { secondaryAssignee, secondaryStartDate, secondaryEndDate } = originalChecklist;
      if (!secondaryAssignee || !secondaryStartDate) {
        console.warn("⚠️ Skipping secondary recurrence generation — missing assignee or dates");
        return;
      }

      console.log("🧩 Generating secondary recurrences for:", secondaryStartDate, secondaryEndDate);
      console.log("🧩 original recurrences length:", originalChecklist);

      // --- 1️⃣ Build the secondary checklist
      const newChecklist = {
        ...originalChecklist,
        assignee: secondaryAssignee,
        startDate: new Date(secondaryStartDate + "T00:00:00.000Z").toISOString(),
        endDate: secondaryEndDate
          ? new Date(secondaryEndDate + "T00:00:00.000Z").toISOString()
          : originalChecklist.endDate,
        originalChecklistId: originalChecklist._id,
        createdBy: auth.username,
        totalWorth: originalChecklist.totalWorth ?? 1,
      };

      // --- 2️⃣ Use backend-generated recurrences (FE does not generate)
      // newChecklist.recurrences will be populated by backend

      if (newChecklist._id)
        delete newChecklist._id;

      await api
        .post(`${process.env.REACT_APP_URL}/checklists`, newChecklist)
        .then(() =>
          console.log(`✅ Secondary checklist created for ${secondaryAssignee}:`, newChecklist.title)
        )
        .catch((err) => console.error("❌ Failed to save secondary checklist", err));

      const updatePayload = {
        updatedBy: auth.username,
        secondaryAssignee, 
        secondaryStartDate: new Date(secondaryStartDate + "T00:00:00.000Z").toISOString(),
        secondaryEndDate: new Date(secondaryEndDate + "T00:00:00.000Z").toISOString(),
      };

      await api
        .put(`${process.env.REACT_APP_URL}/checklists/${primaryChecklist._id}`, updatePayload)
        .then(() =>
          console.log("✅ Primary checklist updated to remove secondary overlap:", primaryChecklist.title)
        )
        .catch((err) => console.error("❌ Failed to update primary checklist", err));

    } catch (err) {
      console.error("❌ generateSecondaryRecurrences() failed:", err);
    }
  }

  const [showMissing, setShowMissing] = useState(false);
  const missingSummary = computeMissingSummary();

  // --- RENDER ---
 return (
  <div style={{ padding: 20 }}>
    <h2>🧾 Checklist Manager</h2>
    <div style={{ display: "flex", gap: 10, marginBottom: 15, flexWrap: "wrap", alignItems: "center" }}>
      {isSuperadmin && (
        <button onClick={openModalForAdd}>➕ Add Task to Checklist</button>
      )}
      <button
        onClick={() => setShowMissing(v => !v)}
        style={{
          background: missingSummary.totalMissing > 0 ? "#fdecea" : "#e8f5e9",
          border: missingSummary.totalMissing > 0 ? "1px solid #f44336" : "1px solid #4caf50",
          borderRadius: 6, padding: "6px 14px", cursor: "pointer", fontWeight: 600,
          color: missingSummary.totalMissing > 0 ? "#c62828" : "#2e7d32", fontSize: 13,
        }}
      >
        {missingSummary.totalMissing > 0
          ? `⚠️ ${missingSummary.totalMissing} Missing Task(s)`
          : "✅ No Missing Tasks"}
      </button>
    </div>

    {showMissing && missingSummary.missingByTask.length > 0 && (
      <div style={{
        background: "#fff3e0", border: "1px solid #ffb74d", borderRadius: 8,
        padding: 12, marginBottom: 15,
      }}>
        <strong style={{ color: "#e65100" }}>Missing Tasks Breakdown:</strong>
        <ul style={{ margin: "8px 0 0 0", paddingLeft: 20 }}>
          {missingSummary.missingByTask.map((mt, i) => (
            <li key={i} style={{ fontSize: 13, marginBottom: 4 }}>
              <strong>{mt.title}</strong> — {mt.assignee || "Unassigned"} — <span style={{ color: "#c62828", fontWeight: 700 }}>{mt.missed} missed</span>
            </li>
          ))}
        </ul>
      </div>
    )}

    {/* -------- FORM (Superadmin Only) -------- */}
      {isSuperadmin && modalOpen && (
      <div style={modalStyles.overlay}>
        <div style={modalStyles.modal}>
          <h3>{editing ? "✏️ Edit Task in Checklist" : "➕ Add New Task for Checklist"}</h3>

          <form onSubmit={handleSave} style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {/* --- Row 1: Title + Assignee --- */}
            <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
              <input
                type="text"
                placeholder="Title"
                value={formData.title}
                onChange={(e) => setFormData(p => ({ ...p, title: e.target.value }))}
                style={{ flex: 1, minWidth: 200 }}
                required
              />
              </div>
           
            {/* --- Row 2: Frequency + Main Start Date --- */}
            <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>

                 <select
                value={formData.assignee}
                onChange={(e) => setFormData(p => ({ ...p, assignee: e.target.value }))}
                style={{ flex: 0.4, minWidth: 180 }}
                required
              >
                <option value="">Add to Checklist of</option>
                {users.map(u => (
                  <option key={u._id} value={u.username}>{u.username}</option>
                ))}
              </select>
            

              <select
                value={formData.frequency}
                onChange={(e) => setFormData(p => ({
                  ...p,
                  frequency: e.target.value,
                  frequencyDays: [],
                }))}
                style={{ flex: 0.4, minWidth: 180 }}
              >
                <option>OneTime</option>
                <option>Daily</option>
                <option>Weekly</option>
                <option>Bi-Weekly</option>
                <option>15-days</option>
                <option>Monthly</option>
                <option>2-Months</option>
                <option>Quarterly</option>
                <option>Bi-Yearly</option>
                <option>Yearly</option>
              </select>
                <br />
             <label> Optional ( Start and End Dates) </label>  
             <br/>          
              <input
                type="date"
                //value={formData.mainStartDate || new Date().toISOString().split("T")[0]} 
                value={formData.mainStartDate || ''} 
                onChange={(e) => setFormData(p => ({ ...p, mainStartDate: e.target.value }))}
                style={{ flex: 0.4, minWidth: 180 }}
                //required
                // Only enforce min (disable past dates) if this is a NEW checklist
                //min={!formData.mainStartDate ? new Date().toISOString().split("T")[0] : undefined}
                placeholder="Optional Start Date"
            
              />
              
               <input
                type="date"
                value={formData.mainEndDate || ""} // optional
                onChange={(e) => setFormData((p) => ({ ...p, mainEndDate: e.target.value }))}
                style={{ flex: 0.4, minWidth: 180 }}                 
                  // Only enforce min (disable past dates) if this is a NEW checklist
                min={!formData.mainEndDate ? new Date().toISOString().split("T")[0] : undefined}
                placeholder="Optional End Date"
              />
            </div>
                <br/>
            {/* --- Weekly / Bi-Weekly Recurrence Days --- */}
            {["Weekly", "Bi-Weekly"].includes(formData.frequency) && (
              <div style={{ marginTop: 10 }}>
                {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(day => (
                  <span
                    key={day}
                    onClick={() => toggleDay(day)}
                    style={{
                      display: "inline-block",
                      margin: "3px",
                      padding: "6px 10px",
                      borderRadius: "5px",
                      cursor: "pointer",
                      backgroundColor: formData.frequencyDays.includes(day) ? "#4caf50" : "#e0e0e0",
                      color: formData.frequencyDays.includes(day) ? "white" : "black",
                    }}
                  >
                    {day}
                  </span>
                ))}
              </div>
            )}

            {/* --- Description --- */}
            <textarea
              placeholder="Description"
              value={formData.description}
              onChange={(e) => setFormData(p => ({ ...p, description: e.target.value }))}
              style={{ width: "100%", marginTop: 10 }}
            />

            {/* --- Secondary Assignee Section --- */}
            <div style={{ marginTop: 10 }}>
              <label>Split Task Dates with new Assignee</label>
              <select
                value={formData.secondaryAssignee || ""}
                onChange={(e) => setFormData(p => ({ ...p, secondaryAssignee: e.target.value }))}
              >
                <option value="">Select Another Assignee</option>
                {users.map(user => (
                  <option key={user._id} value={user.username}>{user.username}</option>
                ))}
              </select>

              {formData.secondaryAssignee && (
                <div style={{ display: "flex", gap: 10, marginTop: 5 }}>
                  <div>
                    <label>Start Date</label>
                    <input
                      type="date"
                      value={formData.secondaryStartDate || ""}
                      onChange={(e) => setFormData(p => ({ ...p, secondaryStartDate: e.target.value }))}
                      required
                       min={new Date().toISOString().split("T")[0]} // ⬅️ disables all past dates
                    />
                  </div>
                  <div>
                    <label>End Date</label>
                    <input
                      type="date"
                      value={formData.secondaryEndDate || ""}
                      onChange={(e) => setFormData(p => ({ ...p, secondaryEndDate: e.target.value }))}
                      required
                       min={new Date().toISOString().split("T")[0]} // ⬅️ disables all past dates
                    />
                  </div>
                </div>
              )}
            </div>

            {/* --- Buttons --- */}
            <div style={{ marginTop: 15, display: "flex", gap: 10 }}>
              <button type="submit">Task to be {editing ? "Updated" : "Created"} for Checklist</button>
              <button
                type="button"
                onClick={() => {
                  resetForm();
                  setEditing(null);
                  setModalOpen(false);
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    )}



    <div style={{ marginBottom: 15 }}>
      <input
        type="text"
        placeholder="🔍 Search by title, description, status, assignee..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        style={{
          width: "100%",
          padding: "8px 12px",
          borderRadius: "6px",
          border: "1px solid #ccc",
          fontSize: 14,
        }}
      />
    </div>

    {/* -------- LIST -------- */}
    {!modalOpen && filteredChecklists.length === 0 ? (
    <p>No checklists yet.</p>
      ) : (!modalOpen &&
      filteredChecklists.map((item) => {
         const recurrences = item.recurrences || [];

        // get month offset for this checklist
        const monthOffset = monthOffsets[item._id] ?? 0;

        const now = new Date();
        const displayMonth = new Date(now.getFullYear(), now.getMonth() + monthOffset, 1);

        const recurrencesThisMonth = recurrences.filter(({ date }) => {
          const d = new Date(date);
          return d.getFullYear() === displayMonth.getFullYear() && d.getMonth() === displayMonth.getMonth();
        });

        return (
          <div
            key={item._id}
            style={{
              border: "1px solid #ddd",
              borderRadius: "8px",
              padding: 15,
              marginBottom: 20,
              background: "#fff",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <h3 style={{ margin: 0 }}>{item.title}</h3>

              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {/* Month navigation arrows */}
                <span onClick={() => changeMonth(item._id, -1)} style={{ cursor: "pointer", fontSize: "22px", marginRight: 8 }}>
                  &lt;
                </span>
                <span style={{ minWidth: "110px", textAlign: "center", fontWeight: 500 }}>
                  {displayMonth.toLocaleString("default", { month: "long", year: "numeric" })}
                </span>
                <span onClick={() => changeMonth(item._id, +1)} style={{ cursor: "pointer", fontSize: "22px", marginRight: 70 }}>
                  &gt;
                </span>

                {isSuperadmin && (
                  <>
                    <FaEdit
                      color="blue"
                      style={{ cursor: "pointer", fontSize: '22px' }}
                      onClick={() => openModalForEdit(item)}
                    />
                    <FaTrash
                      color="red"
                      style={{ cursor: "pointer", fontSize: '22px' }}
                      onClick={() => handleDelete(item._id)}
                    />
                  </>
                )}
              </div>
            </div>

            <p>{item.description}</p>
            <p>
              <strong>:</strong> {item.assignee || "—"} |{" "}
              <strong>:</strong> {item.frequency}
            </p>
            {item.startDate && (
              <p>
                <strong>Start:</strong>{" "}
                {new Date(item.startDate).toLocaleDateString()}
              </p>
            )}

            {/* --- Recurrence Grid (monthly view) --- */}
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "6px",
                justifyContent: "flex-start",
                maxWidth: "100%",
                overflowX: "auto",
                padding: "8px 0",
              }}
            >
              {recurrencesThisMonth.length > 0 ? (
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "4px",
                    justifyContent: "flex-start",
                    overflowX: "auto",
                  }}
                >
                  {recurrencesThisMonth.map(({ date: dateStr, label }, idx) => {
                      const date = new Date(dateStr);
const now = new Date();

const today = new Date();
today.setHours(0, 0, 0, 0);

const d = new Date(date);
d.setHours(0, 0, 0, 0);
  
                    const completion = item.completions?.find(
                      (c) => new Date(c.date).toDateString() === date.toDateString()
                    );

                    const isNA = completion?.notApplicable;
                    const isCompleted = !!completion && !isNA;

                    const isPast = d < today;
const isFuture = d > today;

const isLate =
  !isNA && (completion?.isLate || (isPast && !isCompleted));

const isToday = d.getTime() === today.getTime();

                    const formattedDate = date
                      .toLocaleDateString("en-GB", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "2-digit",
                      })
                      .replace(/\//g, "-");

                    return (
                      <div
                        key={idx}
                        style={{
                          flex: "0 1 calc(12.5% - 4px)",
                          maxWidth: "90px",
                          minWidth: "70px",
                          textAlign: "center",
                          border: "1px solid #bbb",
                          borderRadius: "6px",
                          padding: "2px 1px",
                          background: isNA
                            ? "#f5f5f5"
                            : isCompleted
                            ? "#e8f5e9"
                            : isLate
                            ? "#fdecea"
                            : isToday
                            ? "#e3f2fd"
                            : "#fafafa",
                          color: isNA ? "#9e9e9e" : isToday ? "#0d47a1" : "inherit",
                          fontWeight: isToday ? "bold" : "normal",
                          fontSize: "12px",
                          lineHeight: 1.2,
                          boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
                          opacity: isNA ? 0.7 : 1,
                        }}
                      >
                        <div style={{ marginBottom: "2px" }}>{formattedDate}</div>
                        {isNA && (
                          <span style={{ color: "#9e9e9e", fontSize: "10px", fontWeight: 600 }}>N/A</span>
                        )}
                        {isLate && !isNA && (
                          <span
                            style={{ color: "red", fontSize: "11px", lineHeight: 1 }}
                          >
                            ★
                          </span>
                        )}
                        {!isNA && (
                          <div style={{ marginTop: "2px" }}>
                            <input
                              type="checkbox"
                              style={{ height: "20px", width: "20px", cursor: "pointer" }}
                              checked={isCompleted}
                              disabled={isFuture || isLate}
                              onChange={(e) =>
                                handleRecurrenceTick(item, date, e.target.checked)
                              }
                            />
                          </div>
                        )}
                        {!isCompleted && !isNA && !isFuture && isSuperadmin && (
                          <div style={{ marginTop: "2px" }}>
                            <button
                              onClick={() => handleNotApplicable(item, date)}
                              style={{
                                fontSize: "9px", padding: "1px 4px", background: "#e0e0e0",
                                border: "1px solid #bdbdbd", borderRadius: 3, cursor: "pointer",
                                color: "#616161", fontWeight: 600,
                              }}
                              title="Mark as Not Applicable"
                            >N/A</button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p style={{ color: "#888" }}>No recurrences for this month</p>
              )}
            </div>

            {!isSuperadmin && (
              <button
                onClick={() => handleMarkDone(item)}
                style={{ marginTop: "10px", background: "#4caf50", color: "white" }}
              >
                ✅ Mark Done
              </button>
            )}
          </div>
        );
      })
    )}

  </div>
);

};

export default ChecklistManager;
